import { ExpandMore, InfoOutline, MoreHoriz } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Table as MuiTable,
  TableCell as MuiTableCell,
  styled,
  SvgIconTypeMap,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { isFunction } from 'lodash';
import { ChangeEvent, Fragment, MouseEvent, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { useContainerWidth } from '../../hooks/useContainerWidth';
import { useColumnsWithWidth } from '../../hooks/useColumnsWithWidth';
import { SortOrder } from 'further-types/utils';
import { Row } from '../Layout';
import { ActionsProps } from './TableActions';
import TableSkeleton from './TableSkeleton';
import EllipsisTypography from './EllipsisTypography';
import { RowBase, Column, CheckboxesConfig } from './types';
import {
  BodyRowSelectionPrefix,
  HeaderSelectionPrefix,
} from './RowSelectionPrefix';
import RowActionsContent from './RowActionsContent';

export type { Column };

const ColGroupCol = ({ width }: { width?: string | number }) => (
  <col style={{ width: width ?? 'auto' }} />
);

const Wrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'darkBackground',
})<{ darkBackground?: boolean }>(({ theme, darkBackground }) => ({
  backgroundColor: darkBackground ? theme.palette.grey[50] : 'transparent',
}));

const Container = styled(TableContainer)({
  position: 'relative',
  overflow: 'auto',
  containerType: 'inline-size',
});

const StyledTableHead = styled(TableHead, {
  shouldForwardProp: (prop) => prop !== 'hideHeader',
})<{ hideHeader?: boolean }>(({ hideHeader }) => ({
  visibility: hideHeader ? 'collapse' : undefined,
}));

const RelativeTableBody = styled(TableBody)({
  position: 'relative',
});

export const StyledTable = styled(MuiTable, {
  shouldForwardProp: (prop) =>
    prop !== 'scrollable' && prop !== 'borderless' && prop !== 'hideHeader',
})<{ scrollable?: boolean; borderless?: boolean; hideHeader?: boolean }>(
  ({ theme, scrollable, borderless, hideHeader }) => ({
    ...(scrollable && { tableLayout: 'fixed' }),
    ...(borderless && {
      '& > tbody > tr:last-of-type > td': {
        borderBottom: 'none',
      },
      '& > thead > tr > th:first-of-type, & > tbody > tr > td:first-of-type': {
        borderLeft: 'none',
      },
      '& > thead > tr > th:last-of-type, & > tbody > tr > td:last-of-type': {
        borderRight: 'none',
      },
    }),
    ...(hideHeader && {
      '& > thead > tr > th': {
        borderTop: 'none',
        borderBottom: 'none',
      },
      ...(!borderless && {
        '& > tbody > tr:first-of-type > td': {
          borderTop: `1px solid ${theme.palette.grey[300]}`,
        },
      }),
    }),
  }),
);

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) =>
    prop !== 'isDisabled' &&
    prop !== 'isExpanded' &&
    prop !== 'canClick' &&
    prop !== 'rowBackgroundColor',
})<{
  isDisabled: boolean;
  isExpanded: boolean;
  canClick?: boolean;
  rowBackgroundColor?: string;
}>(({ isDisabled, isExpanded, canClick, rowBackgroundColor }) => ({
  opacity: isDisabled ? 0.5 : 1,
  pointerEvents: 'auto',
  cursor: canClick ? 'pointer' : 'default',
  textDecoration: 'none',
  backgroundColor: rowBackgroundColor,
  '& .MuiTableCell-root': {
    color: isDisabled ? 'text.disabled' : 'inherit',
    borderBottom: isExpanded ? 'none' : undefined,
    backgroundColor: rowBackgroundColor,
  },
  '& .MuiTableCell-root > *': {
    pointerEvents: isDisabled ? 'none' : 'auto',
  },
  '& .MuiIconButton-root': {
    pointerEvents: isDisabled ? 'none' : 'auto',
  },
}));

const EmptyMessageCell = styled(MuiTableCell)(({ theme }) => ({
  padding: theme.spacing(20, theme.spacingSizes.sm),
}));

const EmptyMessageViewportCenter = styled(Box)({
  position: 'sticky',
  left: 0,
  boxSizing: 'border-box',
  // 32px is the Y-padding of the EmptyMessageCell, 2px is the border
  width: 'calc(100cqw - 32px - 2px)',
  maxWidth: 'calc(100cqw - 32px - 2px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const TableBackdrop = styled(Box)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.common.white,
  opacity: 0.4,
}));

const TooltipIcon = styled(InfoOutline)(({ theme }) => ({
  fontSize: theme.fontSizes.lg,
  color: theme.palette.grey[500],
}));

const TableTitle = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const HeaderRow = styled(TableRow, {
  shouldForwardProp: (prop) =>
    prop !== 'withBorder' && prop !== 'withTopBorder',
})<{ withBorder?: boolean; withTopBorder?: boolean }>(
  ({ theme, withBorder, withTopBorder }) => ({
    ...(withBorder && {
      '& th:first-of-type': {
        borderLeft: `1px solid ${theme.palette.grey[300]}`,
      },
      '& th:last-of-type': {
        borderRight: `1px solid ${theme.palette.grey[300]}`,
      },
    }),
    ...(withTopBorder && {
      '& th': {
        borderTop: `1px solid ${theme.palette.grey[300]}`,
      },
    }),
  }),
);

export const TableCell = styled(MuiTableCell, {
  shouldForwardProp: (prop) =>
    prop !== 'paddingY' &&
    prop !== 'noWrap' &&
    prop !== 'textOverflowEllipsis' &&
    prop !== 'sticky' &&
    prop !== 'isHeaderCell' &&
    prop !== 'withVerticalBorder' &&
    prop !== 'isFooterCell' &&
    prop !== 'backgroundColor' &&
    prop !== 'edge' &&
    prop !== 'centered' &&
    prop !== 'minWidth',
})<{
  paddingY?: number;
  edge?: 'start' | 'end';
  noWrap?: boolean;
  textOverflowEllipsis?: boolean;
  sticky?: boolean;
  left?: number;
  isHeaderCell?: boolean;
  isFooterCell?: boolean;
  withVerticalBorder?: boolean;
  backgroundColor?: string;
  centered?: boolean;
  minWidth?: number;
}>(
  ({
    theme,
    paddingY,
    noWrap,
    textOverflowEllipsis,
    width,
    sticky,
    left,
    isHeaderCell,
    withVerticalBorder,
    isFooterCell,
    backgroundColor,
    edge,
    centered,
    minWidth,
  }) => ({
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.h6.fontWeight,
    color: theme.typography.h6.color,
    lineHeight: '18px',
    width,
    minWidth,
    ...(typeof paddingY === 'number' && {
      paddingTop: paddingY,
      paddingBottom: paddingY,
    }),
    ...(typeof edge === 'string' && {
      paddingLeft: edge === 'start' ? 0 : undefined,
      paddingRight: edge === 'end' ? 0 : undefined,
    }),
    ...(sticky && {
      position: 'sticky',
      left,
      backgroundColor: backgroundColor ?? theme.palette.background.paper,
      zIndex: isHeaderCell ? 3 : 2,
    }),
    ...(withVerticalBorder && {
      borderRight: `1px solid ${theme.palette.grey[300]}`,
    }),
    ...(isFooterCell && {
      color: theme.typography.body2.color,
    }),
    whiteSpace: noWrap ? 'nowrap' : 'normal',
    ...(textOverflowEllipsis && width
      ? {
          '& > *': {
            minWidth: width,
            maxWidth: width,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }
      : {
          '& > *': {
            wordBreak: 'break-word',
          },
        }),
    ...(!sticky && {
      backgroundColor,
    }),
    ...(centered && {
      '&&': { verticalAlign: 'middle', textAlign: 'center' },
    }),
  }),
);

const SubRowCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  fontWeight: theme.typography.body2.fontWeight,
}));

const ExpandedContentCell = styled(MuiTableCell)(() => ({
  padding: '0 !important',
}));

const ExpandedContentWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'tableWidth' &&
    prop !== 'darkExpandedContent' &&
    prop !== 'stickyExpandedContent',
})<{
  tableWidth: number | null;
  darkExpandedContent?: boolean;
  stickyExpandedContent?: boolean;
}>(({ theme, tableWidth, darkExpandedContent, stickyExpandedContent }) => ({
  ...(darkExpandedContent && {
    backgroundColor: theme.palette.grey[50],
  }),
  ...(stickyExpandedContent && tableWidth
    ? {
        maxWidth: tableWidth - 2,
        position: 'sticky',
        left: 0,
      }
    : {
        width: '100%',
      }),
}));

export type TableProps<T extends RowBase, OrderByOption extends string> = {
  title?: string | ReactNode;
  columnGroups?: Array<{ label: string; colspan: number }>;
  columns: Array<Column<T>>;
  rows: Array<T>;
  stickyHeader?: boolean;
  isLoading?: boolean;
  emptyMessage?: string | ReactNode;
  rowsPerPageOptions?: Array<number>;
  hidePaginationForSinglePage?: boolean;
  pagination?: {
    page: number;
    rowsPerPage: number;
    orderBy?: OrderByOption;
    order?: SortOrder;
    handleRequestSort: (
      event: MouseEvent<HTMLSpanElement>,
      columnKey: OrderByOption,
    ) => void;
    handleChangePage: (
      e: MouseEvent<HTMLButtonElement> | null,
      page: number,
    ) => void;
    handleChangeRowsPerPage: (e: ChangeEvent<HTMLInputElement>) => void;
  };
  customFooterRow?: Array<{
    key: string;
    colspan?: number;
    backgroundColor?: string;
    content: ReactNode;
  }>;
  displayFooterIfEmptyTable?: boolean;
  disableSorting?: boolean;
  rowsTotalCount?: number;
  scrollable?: boolean;
  actions?: ActionsProps<T>['actions'];
  rowHasPendingAction?: (row: T) => boolean;
  actionsHandleIcon?: OverridableComponent<SvgIconTypeMap<object>>;
  isDisabledRow?: (row: T) => boolean;
  isExpandedRow?: (row: T) => boolean;
  getExpandedContent?: (
    row: T,
    tableWidth: number | null,
    rowIndex: number,
  ) => ReactNode;
  getExpandedRows?: (row: T) => Array<{
    key: string;
    columns: Array<{ key: string; content: ReactNode }>;
  }>;
  disabledRowTooltip?: string | ReactNode | ((row: T) => string | ReactNode);
  onRowClick?: (row: T) => void;
  onRowClickHref?: (row: T) => string | undefined;
  onRowExpand?: (row: T) => void;
  checkboxes?: CheckboxesConfig<T>;
  isExpandableRow?: (row: T) => boolean;
  showHeaderBorder?: boolean;
  showTopBorder?: boolean;
  darkBackground?: boolean;
  darkExpandedContent?: boolean;
  stickyExpandedContent?: boolean;
  borderless?: boolean;
  getRowBackgroundColor?: (row: T) => string | undefined;
  hideHeader?: boolean;
};

const Table = <T extends RowBase, OrderByOption extends string>({
  title,
  columnGroups,
  columns,
  rows,
  stickyHeader = true,
  pagination,
  isLoading = false,
  rowsTotalCount = 0,
  emptyMessage,
  disableSorting = false,
  actions,
  actionsHandleIcon = MoreHoriz,
  isDisabledRow,
  isExpandedRow,
  isExpandableRow,
  getExpandedContent,
  getExpandedRows,
  disabledRowTooltip,
  customFooterRow,
  displayFooterIfEmptyTable = false,
  scrollable = false,
  onRowClick,
  onRowClickHref,
  showHeaderBorder = false,
  showTopBorder = false,
  darkBackground = false,
  darkExpandedContent = false,
  stickyExpandedContent = true,
  borderless = false,
  onRowExpand,
  rowHasPendingAction,
  getRowBackgroundColor,
  hidePaginationForSinglePage = false,
  rowsPerPageOptions = [10, 25, 50, 100],
  checkboxes,
  hideHeader = false,
}: TableProps<T, OrderByOption>): JSX.Element => {
  const history = useHistory();
  const backgroundColor = darkBackground ? grey[50] : undefined;
  const showSkeleton = isLoading && rows.length === 0;
  const showBackdrop = isLoading && !showSkeleton;

  const { width, ref } = useContainerWidth();

  const { order, orderBy, handleRequestSort } = pagination ?? {
    order: SortOrder.Asc,
    orderBy: columns[0]?.key ?? '',
  };

  const enableSorting = !!handleRequestSort && !disableSorting;

  const getSortOrderChangeHandler =
    (columnKey: OrderByOption) => (event: MouseEvent<HTMLSpanElement>) => {
      handleRequestSort?.(event, columnKey);
    };

  const enableStickyHeader = stickyHeader && !!rows.length;
  const showEmptyMessage = !isLoading && rows.length === 0 && !!emptyMessage;

  const hasCheckboxes = Boolean(checkboxes);
  const checkboxesVariant = checkboxes?.variant ?? 'checkbox';
  const hasActions = !!actions?.length || onRowExpand;
  const columnsCount = columns.length;

  const getColumnLabel = (column: Column<T>) => {
    return column.tooltip ? (
      <Tooltip title={column.tooltip} arrow>
        <Box>
          <Row centered spacing="xxs">
            {column.label} <TooltipIcon />
          </Row>
        </Box>
      </Tooltip>
    ) : (
      <>{column.label}</>
    );
  };

  const resolvedColumns = useColumnsWithWidth(columns, width);

  const renderTableHeaderCell = (
    column: Column<T>,
    _index: number,
    prefixContent?: ReactNode,
  ) => {
    const labelContent = enableSorting ? (
      <TableSortLabel
        active={column.key ? orderBy === column.key : false}
        direction={orderBy === column.key ? order : SortOrder.Asc}
        onClick={getSortOrderChangeHandler(column.key as OrderByOption)}
        disabled={isLoading || column.disableSorting}
        IconComponent={ExpandMore}
      >
        <Typography variant="inherit" color="grey.500">
          {getColumnLabel(column)}
        </Typography>
      </TableSortLabel>
    ) : (
      <Typography variant="inherit" color="grey.500">
        {getColumnLabel(column)}
      </Typography>
    );

    return (
      <TableCell
        size="small"
        isHeaderCell
        key={column.key}
        sticky={column.sticky}
        sortDirection={
          enableSorting ? (orderBy === column.key ? order : false) : false
        }
        withVerticalBorder={column.withVerticalBorder}
        left={column.left}
        width={column.width}
        minWidth={column.minWidth}
        noWrap={column.noWrap}
        textOverflowEllipsis={column.textOverflowEllipsis}
        paddingY={column.label ? column.paddingY : 0}
        colSpan={column.colspan}
        backgroundColor={backgroundColor}
      >
        {prefixContent !== undefined ? (
          <Row centered spacing="xs">
            {prefixContent}
            {labelContent}
          </Row>
        ) : (
          labelContent
        )}
      </TableCell>
    );
  };

  const renderTableBodyCell = (
    column: Column<T>,
    row: T,
    rowIndex: number,
    prefixContent?: ReactNode,
    suffixContent?: ReactNode,
  ) => {
    const hasCustomRender = !!column.render;
    const renderedContent = column.render?.(row, rowIndex);
    const value = row[column.key as keyof T];

    const ellipsisTitle = column.textOverflowEllipsis
      ? hasCustomRender
        ? typeof renderedContent === 'string'
          ? renderedContent
          : undefined
        : String(value)
      : undefined;

    const content = hasCustomRender ? (
      typeof renderedContent === 'string' ? (
        ellipsisTitle ? (
          <EllipsisTypography>{renderedContent}</EllipsisTypography>
        ) : (
          <Typography variant="h6">{renderedContent}</Typography>
        )
      ) : (
        renderedContent
      )
    ) : typeof value === 'string' ? (
      ellipsisTitle ? (
        <EllipsisTypography>{value as ReactNode}</EllipsisTypography>
      ) : (
        <Typography variant="h6">{value as ReactNode}</Typography>
      )
    ) : (
      (value as ReactNode)
    );

    return (
      <TableCell
        sticky={column.sticky}
        size="medium"
        left={column.left}
        width={column.width}
        minWidth={column.minWidth}
        key={`${row.id}-${column.key}`}
        noWrap={column.noWrap}
        textOverflowEllipsis={column.textOverflowEllipsis}
        paddingY={column.paddingY}
        withVerticalBorder={column.withVerticalBorder}
        colSpan={column.colspan}
        centered={column.centered}
      >
        {prefixContent !== undefined && suffixContent !== undefined ? (
          <Row justify="space-between" centered>
            <Row centered spacing="xs">
              {prefixContent}
              {content}
            </Row>
            {suffixContent}
          </Row>
        ) : prefixContent !== undefined ? (
          <Row centered spacing="xs">
            {prefixContent}
            {content}
          </Row>
        ) : suffixContent !== undefined ? (
          <Row justify="space-between" centered>
            {content}
            {suffixContent}
          </Row>
        ) : (
          content
        )}
      </TableCell>
    );
  };

  const renderTableFooterCell = (column: Column<T>) => (
    <TableCell
      sticky={column?.sticky}
      size="medium"
      left={column?.left}
      width={column?.width}
      minWidth={column?.minWidth}
      noWrap={column?.noWrap}
      textOverflowEllipsis={column?.textOverflowEllipsis}
      paddingY={column?.paddingY}
      withVerticalBorder={column?.withVerticalBorder}
      isFooterCell
      key={column?.key}
      colSpan={column?.colspan}
    >
      {column.footer ?? null}
    </TableCell>
  );

  const hasFooter =
    (rows.length > 0 || displayFooterIfEmptyTable) &&
    (columns.some((column) => column.footer) || !!customFooterRow?.length);

  const shouldShowPagination =
    pagination &&
    !!rowsTotalCount &&
    (!hidePaginationForSinglePage ||
      Math.ceil(rowsTotalCount / pagination.rowsPerPage) > 1);

  return (
    <Wrapper ref={ref} darkBackground={darkBackground}>
      {title ? (
        <TableTitle>
          {typeof title === 'string' ? (
            <Typography variant="h6">{title}</Typography>
          ) : (
            title
          )}
        </TableTitle>
      ) : null}
      <Container>
        <StyledTable
          scrollable={scrollable}
          stickyHeader={enableStickyHeader}
          borderless={borderless}
          hideHeader={hideHeader}
        >
          {columnGroups ? (
            <colgroup>
              {resolvedColumns.map((column) => (
                <ColGroupCol key={column.key} width={column.width} />
              ))}
            </colgroup>
          ) : null}
          <StyledTableHead hideHeader={hideHeader}>
            {columnGroups ? (
              <HeaderRow
                withTopBorder={showTopBorder}
                withBorder={!!title || showHeaderBorder}
              >
                {columnGroups.map((group) => (
                  <TableCell
                    key={group.label}
                    colSpan={group.colspan}
                    isHeaderCell
                    size="small"
                    backgroundColor={backgroundColor}
                  >
                    <Typography variant="inherit" color="grey.500">
                      {group.label}
                    </Typography>
                  </TableCell>
                ))}
              </HeaderRow>
            ) : null}
            <HeaderRow
              withTopBorder={showTopBorder ? !columnGroups : false}
              withBorder={!!title || showHeaderBorder}
            >
              {resolvedColumns
                .filter((column) => !column.displayAfterActions)
                .map((column, index) =>
                  renderTableHeaderCell(
                    column,
                    index,
                    index === 0 &&
                      checkboxes?.onCheckAllToggle &&
                      checkboxesVariant === 'checkbox' ? (
                      <HeaderSelectionPrefix
                        allRowsChecked={checkboxes.allRowsChecked ?? false}
                        onCheckAllToggle={checkboxes.onCheckAllToggle}
                      />
                    ) : undefined,
                  ),
                )}
              {resolvedColumns
                .filter((column) => column.displayAfterActions)
                .map((column, index) => renderTableHeaderCell(column, index))}
            </HeaderRow>
          </StyledTableHead>
          <RelativeTableBody>
            {showEmptyMessage ? (
              <TableRow>
                <EmptyMessageCell colSpan={columnsCount}>
                  {typeof emptyMessage === 'string' ? (
                    <EmptyMessageViewportCenter>
                      <Typography
                        variant="body2"
                        textAlign="center"
                        color="grey.500"
                        className="table-empty-message"
                      >
                        {emptyMessage}
                      </Typography>
                    </EmptyMessageViewportCenter>
                  ) : (
                    emptyMessage
                  )}
                </EmptyMessageCell>
              </TableRow>
            ) : null}
            {showSkeleton ? (
              <TableSkeleton
                columns={[
                  ...resolvedColumns.filter((col) => !col.displayAfterActions),
                  ...resolvedColumns.filter((col) => col.displayAfterActions),
                ]}
                rowsPerPage={pagination?.rowsPerPage}
              />
            ) : (
              <>
                {rows.map((row, rowIndex) => {
                  const isDisabled = isDisabledRow?.(row) ?? false;
                  const isExpanded = isExpandedRow?.(row) ?? false;
                  const hasPendingAction = rowHasPendingAction?.(row) ?? false;
                  const rowBackgroundColor = getRowBackgroundColor?.(row);
                  const canExpand = isExpandableRow?.(row) ?? false;
                  const rowHref = onRowClickHref?.(row);
                  const canClick = isExpandableRow
                    ? !!onRowClick && isExpandableRow(row)
                    : !!(onRowClick ?? rowHref);

                  const styledRow = (
                    <StyledTableRow
                      isDisabled={isDisabled}
                      isExpanded={isExpanded}
                      rowBackgroundColor={rowBackgroundColor}
                      {...(rowHref && !isDisabled
                        ? { component: 'a', href: rowHref }
                        : {})}
                      onClick={
                        canClick
                          ? (e: MouseEvent) => {
                              if (isDisabled) return;
                              if (e.metaKey || e.ctrlKey || e.button === 1)
                                return;
                              e.preventDefault();
                              if (onRowClick) {
                                onRowClick(row);
                              } else if (rowHref) {
                                history.push(rowHref);
                              }
                            }
                          : undefined
                      }
                      canClick={canClick}
                      hover={canClick}
                    >
                      {resolvedColumns
                        .filter((column) => !column.displayAfterActions)
                        .map((column, columnIndex, nonAfterActionsCols) => {
                          const prefix =
                            columnIndex === 0 && checkboxes ? (
                              <BodyRowSelectionPrefix
                                row={row}
                                rowIndex={rowIndex}
                                checkboxes={checkboxes}
                                variant={checkboxesVariant}
                              />
                            ) : undefined;
                          const isLastColumn =
                            columnIndex === nonAfterActionsCols.length - 1;
                          const suffix =
                            isLastColumn && hasActions ? (
                              <RowActionsContent
                                row={row}
                                hasPendingAction={hasPendingAction}
                                actions={actions}
                                handleIcon={actionsHandleIcon}
                                canExpand={canExpand}
                                isExpanded={isExpanded}
                                onRowExpand={onRowExpand}
                              />
                            ) : undefined;
                          return renderTableBodyCell(
                            column,
                            row,
                            rowIndex,
                            prefix,
                            suffix,
                          );
                        })}
                      {resolvedColumns
                        .filter((column) => column.displayAfterActions)
                        .map((column) =>
                          renderTableBodyCell(column, row, rowIndex),
                        )}
                    </StyledTableRow>
                  );

                  const rowWithTooltip =
                    isDisabled && disabledRowTooltip ? (
                      <Tooltip
                        title={
                          isFunction(disabledRowTooltip)
                            ? disabledRowTooltip(row)
                            : disabledRowTooltip
                        }
                        placement="top"
                      >
                        {styledRow}
                      </Tooltip>
                    ) : (
                      styledRow
                    );

                  return (
                    <Fragment key={row.id}>
                      {rowWithTooltip}
                      {isExpanded ? (
                        getExpandedContent ? (
                          <TableRow>
                            <ExpandedContentCell colSpan={columnsCount}>
                              <ExpandedContentWrapper
                                tableWidth={width}
                                darkExpandedContent={darkExpandedContent}
                                stickyExpandedContent={stickyExpandedContent}
                              >
                                {getExpandedContent(row, width, rowIndex)}
                              </ExpandedContentWrapper>
                            </ExpandedContentCell>
                          </TableRow>
                        ) : getExpandedRows ? (
                          getExpandedRows(row).map((subRow) => (
                            <TableRow key={subRow.key}>
                              {subRow.columns.map((column) => (
                                <SubRowCell key={column.key}>
                                  {column.content}
                                </SubRowCell>
                              ))}
                            </TableRow>
                          ))
                        ) : null
                      ) : null}
                    </Fragment>
                  );
                })}
                {hasFooter ? (
                  customFooterRow ? (
                    <TableRow className="table-custom-footer-row">
                      {hasCheckboxes ? (
                        <TableCell key="footer-checkbox" />
                      ) : null}
                      {customFooterRow.map((cell) => (
                        <TableCell
                          colSpan={cell.colspan}
                          key={cell.key}
                          backgroundColor={cell.backgroundColor}
                        >
                          {cell.content}
                        </TableCell>
                      ))}
                    </TableRow>
                  ) : (
                    <TableRow>
                      {resolvedColumns
                        .filter((column) => !column.displayAfterActions)
                        .map(renderTableFooterCell)}
                      {resolvedColumns
                        .filter((column) => column.displayAfterActions)
                        .map(renderTableFooterCell)}
                    </TableRow>
                  )
                ) : null}
              </>
            )}
          </RelativeTableBody>
        </StyledTable>
        {showBackdrop ? (
          <TableBackdrop>
            <CircularProgress color="inherit" />
          </TableBackdrop>
        ) : null}
      </Container>
      {shouldShowPagination ? (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={rowsTotalCount}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page - 1}
          disabled={isLoading}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        />
      ) : null}
    </Wrapper>
  );
};

export default Table;
